import { marked } from 'marked'
import mjml2html from 'mjml-browser'
import {
  applyEmailImagePlaceholders,
  normalizeEmailImageFieldKeys,
  resolveEditorLogoUrl,
  EMAIL_IMAGE_PLACEHOLDER_EDITORIAL_HERO,
  EMAIL_IMAGE_PLACEHOLDER_HERO
} from './emailImagePlaceholders'
import {
  bulletproofEditorialOutlinePillButtons,
  fixMarkdownCellVisibility,
  injectInviteEmailBodyHtml,
  applyEditorialPillLinkTextColors,
  makeCtaLinksFullyClickable,
  normalizeRuntimePlaceholderLinksInHtml,
  substituteFooterTemplateVariables,
  suppressEmptyOptionalRichtextSections,
  suppressEmptyEditorialStoryButtons,
  suppressEmptyResortDiscoverButtons
} from './inviteEmailTemplateBodyInjector'
import { TERRANEA_EDITORIAL_DEFAULTS } from './templates/shared/editorialEventDefaults.js'
import { resolveTemplateHeroImages } from './templateHeroFields'

const SOCIAL_ICON_BASE_URL = 'https://dashboard.directful.com/social-icons'

export function resolveSocialIconBaseUrl() {
  return SOCIAL_ICON_BASE_URL
}

const RUNTIME_EMAIL_TEMPLATE_PLACEHOLDERS = new Set(['ctaUrl', 'unsubscribeUrl'])

const platformIconFileMap = {
  facebook: 'facebook',
  twitter: 'x',
  x: 'x',
  instagram: 'instagram',
  linkedin: 'linkedin',
  linkedin_alt: 'linkedin',
  youtube: 'youtube'
}

const platformAltMap = {
  facebook: 'Facebook',
  twitter: 'Twitter',
  x: 'Twitter',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  youtube: 'YouTube'
}

function normalizeSocialPlatform(platform) {
  return platform?.toLowerCase?.() || ''
}

function resolveSocialIconFile(platform) {
  const normalized = normalizeSocialPlatform(platform)

  if (normalized === 'twitter' || normalized === 'x') {
    return 'x'
  }

  return platformIconFileMap[normalized] || normalized
}

export function resolveSocialIconUrl(platform, theme = 'light') {
  const iconFile = resolveSocialIconFile(platform)
  const baseUrl = resolveSocialIconBaseUrl()

  return `${baseUrl}/${iconFile}.png`
}

function resolveSocialAlt(platform) {
  const normalized = normalizeSocialPlatform(platform)
  return platformAltMap[normalized] || platform
}

function hasSocialUrl(url) {
  if (typeof url !== 'string') {
    return false
  }

  const trimmed = url.trim().toLowerCase()
  return (
    trimmed.length > 0 &&
    trimmed !== '#' &&
    trimmed !== 'javascript:void(0)' &&
    trimmed !== 'javascript:void(0);'
  )
}

export function normalizeEmailFooterFields(variables = {}) {
  const next = { ...variables }
  const address = `${next.hotelAddress || ''}`.trim()
  const cityStateZip = `${next.hotelCityStateZip || ''}`.trim()

  if (address && cityStateZip) {
    if (address.split(',').length >= 3 || address.includes(cityStateZip)) {
      next.hotelCityStateZip = ''
    }
  }

  return next
}

function hasNonEmptyValue(value) {
  return typeof value === 'string' ? value.trim().length > 0 : value != null && value !== ''
}

function normalizeSocialPlatformFromApi(platform) {
  const normalized = normalizeSocialPlatform(platform)
  return normalized === 'x' ? 'twitter' : normalized
}

export function normalizeNotificationGlobalParams(globalParams = {}) {
  if (!globalParams || typeof globalParams !== 'object') {
    return {}
  }

  const normalized = { ...globalParams }

  if (Array.isArray(globalParams.socialMediaLinks)) {
    normalized.socialMediaLinks = globalParams.socialMediaLinks.map(link => ({
      platform: normalizeSocialPlatformFromApi(link.platform || link.Platform),
      url: link.url || link.Url || ''
    }))
  }

  return normalized
}

export function resolveStoredShellTemplateKey(emailParams = {}) {
  if (!emailParams || typeof emailParams !== 'object') {
    return null
  }

  return emailParams.shellTemplateKey || emailParams.selectedTemplate || null
}

export function resolveSocialFooterTheme(templateKey) {
  if (templateKey === 'resortDiscovery') {
    return { theme: 'light', brandColor: '#4a4a4a' }
  }

  if (templateKey === 'editorialEvent') {
    return { theme: 'editorialNavy', brandColor: '#182852' }
  }

  return { theme: 'light', brandColor: '#3d5168' }
}

export function mergeNotificationEmailPayload(
  prevState,
  emailParams = {},
  globalParams = {},
  { brandLogoUrl } = {}
) {
  const rawParams =
    emailParams && typeof emailParams === 'object'
      ? normalizeEmailImageFieldKeys({ ...emailParams })
      : {}
  // Skip null/undefined so API payloads do not wipe template defaults (e.g. subject).
  const params = Object.fromEntries(
    Object.entries(rawParams).filter(([, value]) => value !== null && value !== undefined)
  )
  const globals = normalizeNotificationGlobalParams(globalParams)

  const merged = {
    ...prevState,
    ...params,
    ...globals,
    richtextFields: {
      ...(prevState.richtextFields || {}),
      ...(params.richtextFields || {})
    }
  }

  if (!hasNonEmptyValue(merged.logoUrl) && hasNonEmptyValue(brandLogoUrl)) {
    merged.logoUrl = brandLogoUrl
  }

  return merged
}

/**
 * Generates HTML for social media links
 * @param {Array} links - Array of social media link objects with platform and url properties
 * @param {'light'|'dark'|'premium'} theme - Footer theme for icon styling
 * @param {string} brandColor - Brand accent color for premium theme
 * @returns {string} HTML string for social media links
 */
export function generateSocialMediaLinksHtml(links, theme = 'light', brandColor = '#3d5168') {
  const activeLinks = (links || []).filter(link => link?.platform && hasSocialUrl(link.url))

  if (!activeLinks.length) {
    return ''
  }

  return activeLinks
    .map(link => {
      const altLabel = resolveSocialAlt(link.platform)
      const href = link.url.trim()
      const iconUrl = resolveSocialIconUrl(link.platform, theme)

      if (theme === 'editorialNavy') {
        return `<a href="${href}" class="social-link" style="text-decoration:none;display:inline-block;margin:0 0 0 10px;vertical-align:middle;">
      <span style="display:inline-block;width:34px;height:34px;border:2px solid #ffffff;border-radius:50%;padding:7px;box-sizing:border-box;line-height:0;background-color:#ffffff;">
        <img src="${iconUrl}" alt="${altLabel}" width="16" height="16" style="display:block;width:16px;height:16px;border:0;outline:none;" />
      </span>
    </a>`
      }

      if (theme === 'dark') {
        return `<a href="${href}" class="social-link" style="text-decoration: none; display: inline-block; margin: 0 8px;">
      <span style="display: inline-block; width: 42px; height: 42px; border: 2px solid #ffffff; border-radius: 50%; text-align: center; line-height: 38px;">
        <img src="${iconUrl}" alt="${altLabel}" style="width: 18px; height: 18px; vertical-align: middle; opacity: 1; display: inline-block;" />
      </span>
    </a>`
      }

      if (theme === 'premium') {
        return `<a href="${href}" class="social-link" style="text-decoration: none; display: inline-block; margin: 0 10px;">
      <span style="display: inline-block; width: 40px; height: 40px; background: ${brandColor}; border-radius: 4px; text-align: center; line-height: 40px;">
        <img src="${iconUrl}" alt="${altLabel}" style="width: 18px; height: 18px; vertical-align: middle; display: inline-block;" />
      </span>
    </a>`
      }

      return `<a href="${href}" class="social-link" style="text-decoration: none; margin: 0 6px; display: inline-block;">
      <img src="${iconUrl}" alt="${altLabel}" style="width: 28px; height: 28px; opacity: 0.55; display: inline-block;" />
    </a>`
    })
    .join('\n')
}

function resolveLearnMoreLinkFields(variables = {}) {
  const resolved = { ...variables }
  const col1Text = String(resolved.col1LinkText || '').trim()
  const col2Text = String(resolved.col2LinkText || '').trim()
  const col3Text = String(resolved.col3LinkText || '').trim()

  resolved.col1LinkUrl =
    resolved.col1LinkUrl || resolved.col1ImageLinkUrl || resolved.ctaUrl || '#'
  resolved.col2LinkUrl =
    resolved.col2LinkUrl || resolved.col2ImageLinkUrl || resolved.ctaUrl || '#'
  resolved.col3LinkUrl =
    resolved.col3LinkUrl || resolved.col3ImageLinkUrl || resolved.ctaUrl || '#'
  resolved.col1LinkHiddenClass = col1Text ? '' : ' text-link-arrow-empty'
  resolved.col2LinkHiddenClass = col2Text ? '' : ' text-link-arrow-empty'
  resolved.col3LinkHiddenClass = col3Text ? '' : ' text-link-arrow-empty'

  return resolved
}

function resolveEditorialCtaColors(variables = {}) {
  const resolved = { ...variables }
  const borderColor = `${resolved.ctaBackgroundColor || TERRANEA_EDITORIAL_DEFAULTS.ctaBackgroundColor}`.trim()
  let textColor = `${resolved.ctaTextColor || ''}`.trim()

  if (!textColor || textColor === '#000000' || textColor === '#ffffff') {
    textColor = borderColor
  }

  resolved.ctaBackgroundColor = borderColor
  resolved.ctaTextColor = textColor

  return resolved
}

function resolveEditorialStoryLinkFields(variables = {}) {
  const resolved = resolveLearnMoreLinkFields(variables)

  if (!`${resolved.ctaText || ''}`.trim()) {
    resolved.ctaText = TERRANEA_EDITORIAL_DEFAULTS.ctaText
  }

  ;['col1', 'col2', 'col3'].forEach(columnKey => {
    const textKey = `${columnKey}LinkText`

    if (!`${resolved[textKey] || ''}`.trim()) {
      resolved[textKey] = TERRANEA_EDITORIAL_DEFAULTS[textKey] || ''
    }
  })

  return resolveEditorialCtaColors(resolved)
}

/**
 * Converts MJML to HTML with variable substitution
 * @param {string} mjml - MJML template string
 * @param {Object} variables - Variables to substitute in the template
 * @param {Object} html - Optional HTML content object with keys and corresponding HTML
 * @returns {string} Generated HTML string
 */
function isTemplateVariableValue(value) {
  return (
    value == null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
}

function substituteTemplateVariables(mjml, templateVariables) {
  let populatedMjml = mjml

  Object.entries(templateVariables).forEach(([key, value]) => {
    if (!isTemplateVariableValue(value)) {
      return
    }

    const regex = new RegExp(`{{${key}}}`, 'g')
    const replacement = RUNTIME_EMAIL_TEMPLATE_PLACEHOLDERS.has(key)
      ? `{{${key}}}`
      : `${value ?? ''}`
    populatedMjml = populatedMjml.replace(regex, () => replacement)
  })

  return populatedMjml
}

export const getHtmlFromMjml = (mjml, variables, html = {}, options = {}) => {
  const useImagePlaceholders = options.useImagePlaceholders !== false
  const templateKey = options.templateKey || variables.selectedTemplate || variables.shellTemplateKey
  const brandLogoUrl = options.brandLogoUrl || ''
  const normalizedVariables = normalizeEmailImageFieldKeys(variables)
  normalizedVariables.logoUrl = resolveEditorLogoUrl(normalizedVariables, brandLogoUrl)
  const withHeroImages = resolveTemplateHeroImages(normalizedVariables, templateKey)
  const templateVariables =
    templateKey === 'editorialEvent'
      ? resolveEditorialStoryLinkFields(
          useImagePlaceholders ? applyEmailImagePlaceholders(withHeroImages) : withHeroImages
        )
      : resolveLearnMoreLinkFields(
          useImagePlaceholders ? applyEmailImagePlaceholders(withHeroImages) : withHeroImages
        )

  if (templateKey === 'resortDiscovery') {
    templateVariables.footerLogoWidth =
      templateVariables.footerLogoWidth || templateVariables.logoWidth || '120px'
  }

  if (templateKey === 'editorialEvent') {
    const heroUrl = `${templateVariables.heroImgUrl || ''}`.trim()
    if (!heroUrl || heroUrl === EMAIL_IMAGE_PLACEHOLDER_HERO) {
      templateVariables.heroImgUrl = EMAIL_IMAGE_PLACEHOLDER_EDITORIAL_HERO
    }
  }

  const populatedMjml = substituteTemplateVariables(mjml, templateVariables)
  const { html: generatedHtml, errors } = mjml2html(populatedMjml, {
    validationLevel: 'soft',
    keepComments: true
  })

  if (errors && errors.length > 0) {
    console.error('MJML Errors:', errors)
    alert('There were errors generating the email HTML. Check console for details.')
    return ''
  }

  let finalHtml = generatedHtml
  let bodyHtmlInjectedViaPlaceholder = false

  Object.entries(html).forEach(([key, content]) => {
    const placeholder = `<!-- ${key} -->`

    if (key === 'bodyHtml' && finalHtml.includes(placeholder)) {
      bodyHtmlInjectedViaPlaceholder = true
    }

    finalHtml = finalHtml.replace(placeholder, content || '')
  })

  if (html.bodyHtml && !bodyHtmlInjectedViaPlaceholder) {
    finalHtml = injectInviteEmailBodyHtml(finalHtml, html.bodyHtml)
  } else {
    finalHtml = fixMarkdownCellVisibility(finalHtml)
  }

  const processedHtml =
    templateKey === 'resortDiscovery'
      ? suppressEmptyResortDiscoverButtons(
          suppressEmptyOptionalRichtextSections(finalHtml, html),
          templateVariables
        )
      : templateKey === 'editorialEvent'
        ? applyEditorialPillLinkTextColors(
            suppressEmptyEditorialStoryButtons(
              suppressEmptyOptionalRichtextSections(finalHtml, html),
              templateVariables
            ),
            `${templateVariables.ctaTextColor || templateVariables.ctaBackgroundColor || '#00a7b5'}`.trim()
          )
        : suppressEmptyOptionalRichtextSections(finalHtml, html)

  return makeCtaLinksFullyClickable(
    normalizeRuntimePlaceholderLinksInHtml(processedHtml)
  )
}

export function prepareEditorialEmailHtml(html, templateKey, variables = {}) {
  const withFooter = substituteFooterTemplateVariables(html, variables)

  if (templateKey !== 'editorialEvent') {
    return makeCtaLinksFullyClickable(withFooter)
  }

  const textColor = `${variables.ctaTextColor || variables.ctaBackgroundColor || '#00a7b5'}`.trim()

  return makeCtaLinksFullyClickable(
    applyEditorialPillLinkTextColors(
      bulletproofEditorialOutlinePillButtons(withFooter, {
        borderColor: `${variables.ctaBackgroundColor || '#00a7b5'}`.trim(),
        textColor
      }),
      textColor
    )
  )
}

export function extractPreviewBodyHtml(html) {
  if (!html) {
    return html
  }

  const styleBlocks = [...html.matchAll(/<style[^>]*>[\s\S]*?<\/style>/gi)]
    .map(match => match[0])
    .join('')

  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
  if (bodyMatch) {
    return `${styleBlocks}${bodyMatch[1].trim()}`
  }

  return html
}

/**
 * Replaces token syntax with template variable syntax
 * @param {string} bodyContent - Content with token syntax
 * @returns {string} Content with template variable syntax
 */
export function replaceTagsSyntax(bodyContent) {
  return bodyContent.replace(/:token\{key="(.*?)"\s+label="(.*?)"\}/g, (match, p1) => {
    if (p1 === 'guestName') {
      return '{{firstName}}'
    }

    if (p1 === 'guestLastName') {
      return '{{lastName}}'
    }

    if (p1 === 'hotelName') {
      return '{{hotelName}}'
    }

    if (p1 === 'checkinDate') {
      return '{{checkIn}}'
    }

    if (p1 === 'checkoutDate') {
      return '{{checkOut}}'
    }

    return match
  })
}

/**
 * Replaces template variables with preview content
 * @param {string} bodyContent - Content with template variables
 * @param {string} userName - User name for preview
 * @param {string} hotelName - Hotel name for preview
 * @returns {string} Content with preview values
 */
export function replaceTagsWithPreviewContent(bodyContent, userName, hotelName) {
  return (bodyContent || '').replace(/{{(.*?)}}/g, (match, p1) => {
    if (p1 === 'firstName') {
      return userName?.split(' ')[0] || 'Guest'
    }

    if (p1 === 'lastName') {
      return userName?.split(' ')[1] || ''
    }

    if (p1 === 'guestName') {
      return userName || 'Guest'
    }

    if (p1 === 'hotelName') {
      return hotelName || 'your hotel'
    }

    if (p1 === 'checkIn') {
      return 'July 15, 2026'
    }

    if (p1 === 'checkOut') {
      return 'December 29, 2024'
    }

    if (p1 === 'ctaUrl' || p1 === 'unsubscribeUrl') {
      return '#'
    }

    return match
  })
}

/**
 * Generates HTML for email with all processing
 * @param {Object} params - Parameters for email generation
 * @param {string} params.mjmlTemplate - MJML template string
 * @param {Object} params.variables - Variables to substitute in the template
 * @param {string} params.body - Email body content
 * @param {Array} params.socialMediaLinks - Social media links array
 * @returns {string} Generated HTML
 */
export function generateEmailHtml({ mjmlTemplate, variables, body, socialMediaLinks }) {
  return getHtmlFromMjml(mjmlTemplate, variables, {
    bodyHtml: marked.parse(replaceTagsSyntax(body)),
    socialMediaLinksHtml: generateSocialMediaLinksHtml(socialMediaLinks)
  })
}

/**
 * Extracts social media URLs from links array
 * @param {Array} socialMediaLinks - Array of social media link objects
 * @returns {Object} Object with platform-specific URLs
 */
export function extractSocialMediaUrls(socialMediaLinks) {
  const findUrl = (...platforms) => {
    const link = socialMediaLinks.find(item =>
      platforms.includes(normalizeSocialPlatform(item.platform))
    )
    return link?.url || ''
  }

  return {
    facebookUrl: findUrl('facebook'),
    twitterUrl: findUrl('twitter', 'x'),
    instagramUrl: findUrl('instagram'),
    linkedinUrl: findUrl('linkedin'),
    youtubeUrl: findUrl('youtube')
  }
}

/**
 * Creates a state updater function for a specific property
 * @param {Function} setEmailState - The state setter function
 * @param {string} property - The property name to update
 * @returns {Function} A function that updates the specific property
 */
export function createEmailStateUpdater(setEmailState, property) {
  return value => {
    setEmailState(prevState => ({
      ...prevState,
      [property]: value
    }))
  }
}

/**
 * Creates multiple state updater functions for email properties
 * @param {Function} setEmailState - The state setter function
 * @param {Array} properties - Array of property names
 * @returns {Object} Object with updater functions for each property
 */
export function createEmailStateUpdaters(setEmailState, properties) {
  const updaters = {}
  properties.forEach(property => {
    updaters[`set${property.charAt(0).toUpperCase() + property.slice(1)}`] =
      createEmailStateUpdater(setEmailState, property)
  })
  return updaters
}
